import { useCallback, useState } from 'react';
import companyApi from 'lib/api/company';
import handleErrorMessageAPI from 'global/AlertErrorMessage';
import ERROR_MESSAGE from 'constants/errorMessage';
import useAsync from 'hooks/useAsync';

type UseRunningTradeInitOptionsV1 = {
  version: 'V1';
  defaultParams?: GetInitRunningTradeParams;
  onSuccess?: (data: GetInitRunningTradeRes) => void;
};

type UseRunningTradeInitOptionsV2 = {
  version: 'V2';
  defaultParams?: GetInitRunningTradeParamsV2;
  onSuccess?: (data: GetInitRunningTradeRes) => void;
};

type UseRunningTradeInitOptions = UseRunningTradeInitOptionsV1 | UseRunningTradeInitOptionsV2;

const fetchRunningTrade = async (params: GetInitRunningTradeParams) => {
  const res = await companyApi.getInitRunningTrade(params);

  if (!res.data) {
    throw new Error('Attempt to get init of Running Trades Failed');
  }

  const {
    data: { message, error, data },
  } = res;

  if (error) {
    handleErrorMessageAPI(message, ERROR_MESSAGE.ALERT_RED);
    throw new Error(message);
  }

  return data;
};

const fetchRunningTradeV2 = async (params: GetInitRunningTradeParamsV2) => {
  const res = await companyApi.getInitRunningTradeV2(params);

  if (!res.data) {
    throw new Error('Attempt to get init of Running Trades Failed');
  }

  const {
    data: { message, error, data },
  } = res;

  if (error) {
    handleErrorMessageAPI(message, ERROR_MESSAGE.ALERT_RED);
    throw new Error(message);
  }

  return data;
};

const useRunningTradeInit = ({
  version,
  defaultParams,
  onSuccess,
}: UseRunningTradeInitOptions) => {
  const isV2 = version === 'V2';

  const [params, setParams] =
    useState<GetInitRunningTradeParams>(defaultParams);
  const [runningTrades, setRunningTrades] = useState<GetInitRunningTradeItem[]>(
    [],
  );

  const {
    value: runningTradeRes,
    error,
    loading: isLoading,
  } = useAsync(async () => {
    const data = isV2
      ? await fetchRunningTradeV2(params)
      : await fetchRunningTrade(params);

    setRunningTrades((s) => [
      ...(params.trade_number ? s : []),
      ...data.running_trade,
    ]);
    if (onSuccess) onSuccess(data);
    return data;
  }, [params]);

  const loadMore = useCallback(() => {
    setTimeout(
      () => {
        const cursor = runningTrades[runningTrades.length - 1].trade_number;
        setParams((s) => ({ ...s, trade_number: cursor }));
      },
      error ? 1000 : 0,
    );
  }, [runningTrades, error]);

  const reload = useCallback(() => {
    setTimeout(
      () => {
        setParams((s) => ({ ...s }));
      },
      error ? 1000 : 0,
    );
  }, [runningTrades, error]);

  return {
    runningTradeRes,
    runningTrades,
    isInitiating: isLoading && !error && !runningTradeRes,
    isLoadingMore: isLoading && runningTrades.length > 0,
    hasMore:
      (runningTradeRes && runningTradeRes.running_trade.length > 0) || !!error,
    loadMore,
    params,
    setParams,
    reload,
  };
};
export default useRunningTradeInit;
